// SkipInventoryUtilisationPage — /reports/skip-management/inventory.
//
// Fleet status, availability and per-unit usage. KPI cards + two bar charts
// (units by status, units by type) + a sortable per-unit table. Fleet status
// counts are point-in-time; jobs_in_period is bounded by the date range.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Container } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { DateRangeFilter, lastThirtyDays } from './components/DateRangeFilter';
import { KpiGrid, type KpiTile } from './components/KpiGrid';
import { BarChart } from './components/BarChart';
import { SortableTable, type SortableColumn } from './components/SortableTable';
import { formatNumber, pct } from './components/format';
import { useInventoryUtilisation, type DateRange } from './useSkipReports';
import type { ReportCell } from '../useReports';
import styles from './skipReports.module.css';

const TABLE_COLUMNS: SortableColumn[] = [
  { key: 'skip_number', label: 'Unit', type: 'string' },
  { key: 'stock_type_name', label: 'Type', type: 'string' },
  { key: 'size_name', label: 'Size', type: 'string' },
  { key: 'status', label: 'Status', type: 'string' },
  { key: 'skip_condition', label: 'Condition', type: 'string' },
  { key: 'jobs_in_period', label: 'Jobs in period', type: 'number' },
];

export function SkipInventoryUtilisationPage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<DateRange>(lastThirtyDays());
  const [submitted, setSubmitted] = useState<DateRange>(form);
  const enabled = !!submitted.date_from && !!submitted.date_to;

  const query = useInventoryUtilisation(submitted, enabled);
  const data = query.data ?? null;

  const kpis = useMemo<KpiTile[]>(() => {
    if (!data) return [];
    return [
      { label: 'Total units', value: formatNumber(data.kpis.total_units), tone: 'blue' },
      { label: 'Currently out', value: formatNumber(data.kpis.out_units), tone: 'amber' },
      { label: 'Available', value: formatNumber(data.kpis.available_units), tone: 'green' },
      { label: 'Utilisation', value: pct(data.kpis.utilisation_rate), tone: 'teal' },
    ];
  }, [data]);

  const tableRows = useMemo<Record<string, ReportCell>[]>(
    () =>
      (data?.units ?? []).map((u) => ({
        skip_number: u.skip_number,
        stock_type_name: u.stock_type_name,
        size_name: u.size_name,
        status: u.status,
        skip_condition: u.skip_condition,
        jobs_in_period: u.jobs_in_period,
      })),
    [data],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Inventory Utilisation"
        subtitle="Fleet status and per-unit usage over a date range"
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/reports/skip-management')}>
            Back
          </Button>
        }
      />

      <DateRangeFilter
        form={form}
        onChange={setForm}
        onSubmit={() => setSubmitted({ ...form })}
        loading={query.isFetching}
      />

      {query.isFetching && !data ? (
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      ) : null}

      {data ? <KpiGrid tiles={kpis} /> : null}

      {data ? (
        <Card title="Units by status">
          <BarChart
            data={data.by_status.map((r) => ({ label: r.status, value: r.units }))}
            colour="var(--color-secondary, #2563eb)"
          />
        </Card>
      ) : null}

      {data ? (
        <Card title="Units by type">
          <BarChart
            data={data.by_type.map((r) => ({ label: r.type_name, value: r.units }))}
            colour="var(--color-status-confirmed_text, #1e40af)"
          />
        </Card>
      ) : null}

      {data ? (
        <Card title="Per-unit usage" flush>
          <SortableTable
            columns={TABLE_COLUMNS}
            rows={tableRows}
            exportName="skip-inventory-utilisation"
            emptyMessage="No inventory units found."
          />
        </Card>
      ) : null}

      {!query.isFetching && !data ? (
        <Card>
          <p className={styles.empty}>
            <Container size={20} aria-hidden /> Pick a date range and refresh to see the report.
          </p>
        </Card>
      ) : null}
    </div>
  );
}
